import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, Tag, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const res = await axios.get(`${API}/public/blogs/${slug}`);
      // Normalize non-breaking spaces (common in pasted content) so text wraps properly
      const data = { ...res.data };
      if (typeof data.content === 'string') {
        data.content = data.content.replace(/(&nbsp;|\u00a0)/g, ' ');
      }
      setBlog(data);
      // Set document title for SEO
      if (data.meta_title) {
        document.title = data.meta_title;
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-zinc-500">Loading article...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <h1 className="text-4xl font-bold">Article Not Found</h1>
        <p className="text-zinc-400">The blog post you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/blog')}
          className="mt-4 px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-medium transition-all"
        >
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-detail-page">
      {/* SEO Meta Tags */}
      {blog.meta_description && (
        <meta name="description" content={blog.meta_description} />
      )}

      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">Law Agency 360</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/blog')}
                className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
              >
                Blog
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-violet-500/25"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="relative">
        {/* Hero Image */}
        {blog.featured_image && (
          <div className="w-full h-[400px] relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black z-10"></div>
            <img
              src={blog.featured_image}
              alt={blog.title}
              className="w-full h-full object-cover"
              data-testid="blog-featured-image"
            />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
          {/* Back link */}
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-zinc-500 hover:text-violet-400 transition-colors mb-8 text-sm"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-testid="blog-title">
            {blog.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-4 text-zinc-500 text-sm mb-10 pb-10 border-b border-white/10">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {blog.published_at && new Date(blog.published_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric'
              })}
            </div>
            {blog.target_keyword && (
              <div className="flex items-center gap-1.5">
                <Tag className="w-4 h-4" />
                {blog.target_keyword}
              </div>
            )}
          </div>

          {/* Content */}
          <div
            className="blog-body max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content }}
            data-testid="blog-content"
          />
        </div>
      </article>

      {/* CTA */}
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
        <div className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 backdrop-blur-xl border border-violet-500/20 rounded-3xl p-10 text-center">
          <h3 className="text-2xl font-bold mb-3">Ready to improve your visibility?</h3>
          <p className="text-zinc-400 mb-6">See exactly what your marketing agency is doing for you.</p>
          <button
            onClick={() => navigate('/auth')}
            className="px-8 py-3.5 bg-violet-600 hover:bg-violet-500 text-white rounded-2xl font-semibold transition-all hover:shadow-2xl hover:shadow-violet-500/30"
            data-testid="blog-cta"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative py-12 px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center text-zinc-500 text-sm">
          &copy; 2026 Law Agency 360. All rights reserved.
        </div>
      </footer>

      {/* Blog content typography */}
      <style>{`
        .blog-body {
          color: #d4d4d8;
          font-size: 1.125rem;
          line-height: 1.85;
          overflow-wrap: break-word;
          word-break: break-word;
          hyphens: auto;
        }
        .blog-body * { max-width: 100%; }
        .blog-body pre, .blog-body table { overflow-x: auto; display: block; }
        .blog-body > *:first-child { margin-top: 0; }
        .blog-body h1, .blog-body h2, .blog-body h3, .blog-body h4 {
          color: #ffffff;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.25;
        }
        .blog-body h1 { font-size: 2.25rem; margin: 2.5rem 0 1rem; }
        .blog-body h2 { font-size: 1.75rem; margin: 2.25rem 0 1rem; }
        .blog-body h3 { font-size: 1.375rem; margin: 2rem 0 0.75rem; }
        .blog-body h4 { font-size: 1.125rem; margin: 1.5rem 0 0.5rem; }
        .blog-body p { margin: 0 0 1.25rem; }
        .blog-body a { color: #a78bfa; text-decoration: none; }
        .blog-body a:hover { text-decoration: underline; }
        .blog-body strong { color: #ffffff; font-weight: 600; }
        .blog-body ul, .blog-body ol { margin: 0 0 1.25rem; padding-left: 1.5rem; color: #d4d4d8; }
        .blog-body ul { list-style: disc; }
        .blog-body ol { list-style: decimal; }
        .blog-body li { margin: 0.4rem 0; }
        .blog-body blockquote {
          border-left: 4px solid #7c3aed;
          padding: 0.25rem 0 0.25rem 1.25rem;
          margin: 1.5rem 0;
          color: #a1a1aa;
          font-style: italic;
        }
        .blog-body img { border-radius: 0.75rem; margin: 1.5rem 0; max-width: 100%; height: auto; }
        .blog-body hr { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 2rem 0; }
        .blog-body code {
          background: rgba(255,255,255,0.08);
          padding: 0.15rem 0.4rem;
          border-radius: 0.35rem;
          font-size: 0.9em;
          color: #e9d5ff;
        }
        .blog-body pre {
          background: #18181b;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.75rem;
          padding: 1rem 1.25rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        .blog-body pre code { background: none; padding: 0; color: #e4e4e7; }
        .blog-body table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; }
        .blog-body th, .blog-body td { border: 1px solid rgba(255,255,255,0.1); padding: 0.6rem 0.85rem; text-align: left; }
        .blog-body th { background: rgba(255,255,255,0.04); color: #fff; font-weight: 600; }
      `}</style>
    </div>
  );
}
